/**
 * AssigneePicker - dropdown that lets an agent pick who a ticket is
 * assigned to (SPEC §9 detail header).
 *
 * The picker shows:
 *   - "Unassigned" (clears the FK)
 *   - "Mine" (resolves to the current agent's id on PATCH; the value
 *     ``"mine"`` flows through unchanged so server-side filter routes
 *     like ``GET /agent/tickets?assignee_user_id=mine`` keep working)
 *   - "All" (filter-only, suppressed in detail-header context)
 *   - one row per active agent + system_admin from GET /agent/agents,
 *     alphabetical by name
 *
 * The agent directory is fetched via ``useAgents`` and cached for the
 * session. When the fetch is still in flight OR fails, the picker
 * degrades gracefully to just the three static options so the agent
 * can still reassign to themselves or unassign without waiting.
 */
import Select from "@/components/ui/Select";
import { useAgents } from "@/hooks/useAgents";

export interface AssigneePickerProps {
  /**
   * Current value. Encoding:
   *   "mine"        -> current agent (resolved server-side on filter
   *                    queries; on PATCH the caller passes their own
   *                    user id explicitly).
   *   "unassigned"  -> assignee_user_id = NULL
   *   "all"         -> filter only; not a valid PATCH value.
   *   any other     -> agent user UUID.
   */
  value: string;
  onChange: (value: string) => void;
  /**
   * Whether to include the "All" option (filter mode). Disabled in the
   * detail-header context where "All" is not meaningful.
   */
  includeAll?: boolean;
  /** Visible label above the select. */
  label?: string;
  id?: string;
}

export function AssigneePicker({
  value,
  onChange,
  includeAll = false,
  label,
  id,
}: AssigneePickerProps): JSX.Element {
  const agents = useAgents();

  const options: { value: string; label: string }[] = [
    { value: "unassigned", label: "Unassigned" },
    { value: "mine", label: "Mine" },
    ...(includeAll ? [{ value: "all", label: "All" }] : []),
  ];

  const directory = agents.data ?? [];
  if (directory.length > 0) {
    // Visual separator between the special-case options ("Unassigned",
    // "Mine", optional "All") and the real agent rows. Disabled so the
    // user can't pick it; the dashes read clearly in a native <select>.
    options.push({ value: "__sep__", label: "──────────" });
    for (const a of directory) {
      options.push({
        value: a.id,
        label: a.full_name || a.email,
      });
    }
  }

  return (
    <Select
      id={id}
      label={label}
      options={options}
      value={value}
      onChange={(e) => {
        // Defend against the user somehow landing on the separator (it
        // is HTML-disabled but a programmatic change could still set
        // the value). Drop the event so the parent does not PATCH with
        // a sentinel string.
        if (e.target.value === "__sep__") return;
        onChange(e.target.value);
      }}
    />
  );
}

export default AssigneePicker;
